import { useQuery } from "@tanstack/react-query";
import { ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import { usePageTitle } from "@/lib/title";
import type { AuditLogEntry } from "@/types";
import { Card, CardBody } from "@/components/ui/Card";
import { FullPageSpinner } from "@/components/ui/Spinner";
import { EmptyState } from "@/components/ui/EmptyState";

export function AuditLogsPage() {
  usePageTitle("Audit Logs");
  const { data, isLoading } = useQuery({
    queryKey: ["audit-logs"],
    queryFn: () => api.getPaginated<AuditLogEntry>("/api/v1.0/audit-logs?page=1&page_size=50"),
  });

  if (isLoading) return <FullPageSpinner />;

  return (
    <div className="flex flex-col gap-6">
      <p className="-mt-3 text-sm text-slate-500">Track sensitive actions across the platform.</p>

      {!data?.data.length ? (
        <EmptyState icon={ShieldCheck} title="No audit events yet" />
      ) : (
        <Card>
          <CardBody className="p-0">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-100 text-left text-xs uppercase text-slate-400">
                <tr>
                  <th className="px-5 py-3">Time</th>
                  <th className="px-5 py-3">Actor</th>
                  <th className="px-5 py-3">Action</th>
                  <th className="px-5 py-3">Entity</th>
                  <th className="px-5 py-3">IP</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.data.map((log) => (
                  <tr key={log.id}>
                    <td className="px-5 py-3 text-slate-500">{new Date(log.created_at).toLocaleString()}</td>
                    <td className="px-5 py-3">
                      {log.actor_name ? (
                        <div>
                          <p className="font-medium text-slate-800">{log.actor_name}</p>
                          <p className="text-xs text-slate-400">{log.actor_email}</p>
                        </div>
                      ) : (
                        <span className="text-slate-400">system</span>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs">{log.action}</code>
                    </td>
                    <td className="px-5 py-3 text-slate-600">{log.entity_type}</td>
                    <td className="px-5 py-3 text-slate-400">{log.ip_address || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardBody>
        </Card>
      )}
    </div>
  );
}
